import React, { useEffect, useState } from "react";

import { useAppStore } from "../../../../../context/store";
import { Dialog, Portal, CloseButton, Button, Input, Spinner, Field, Textarea } from "@chakra-ui/react";
import { removeEpochSuffix, removeFileExtension, showToast } from "../../../../../utilies/Utility";
import Type from "../../../../../utilies/Types";
import { getEditedGeoJSON } from "../../../../../utilies/geojson/GetEditedGeojson";
import { diffGeoJSON } from "../../../../../utilies/geojson/GetDiffrentGeojson";
import { useMapObjects } from "../../../../../context/MapObjectsContext";
import { saveLayerToServerUnified } from "../../../../../utilies/loader/layer/saveLayerToServer";

const LayersSave = () => {
  const showLayersSaveDialog = useAppStore((s) => s.showLayersSaveDialog);
  const currentLayer = useAppStore((s) => s.currentLayer);
  const userData = useAppStore((s) => s.userData);
  const { mapObjects, setMapObjects } = useMapObjects();
  const autoLayersSave = useAppStore((s) => s.autoLayersSave);
  const setAutoLayersSave = useAppStore((s) => s.setAutoLayersSave);

  const pointCloudStyle = useAppStore((s) => s.pointCloudStyle);
  const setShowLayersSaveDialog = useAppStore.getState().setShowLayersSaveDialog;
  const setCurrentLayer = useAppStore.getState().setCurrentLayer;

  const [fileName, setFileName] = useState("");
  const [description, setDescription] = useState("");
  const [timeStamp, setTimeStamp] = useState(Math.floor(Date.now() / 1000));
  const [isProgress, setIsProgress] = useState(false);

  useEffect(() => {
    if (showLayersSaveDialog && currentLayer) {
      setIsProgress(false);
      if (currentLayer.description) {
        setDescription(currentLayer.description);
      }

      let localFileName = "";
      if (currentLayer.type != Type.ASSET.GEOJSON) {
        setFileName(currentLayer.file_name);
      } else {
        let changeset = diffGeoJSON(currentLayer.original_geojson, getEditedGeoJSON(currentLayer));

        if (changeset.length != 0) {
          localFileName = removeEpochSuffix(removeFileExtension(currentLayer.file_name)) + "_" + timeStamp + currentLayer.file_type;
        } else {
          localFileName = currentLayer.file_name;
        }
        setFileName(localFileName);
      }

      if (autoLayersSave) {
        saveLayerToServer(localFileName);
      }
    } else {
      setTimeStamp(Math.floor(Date.now() / 1000));
    }
  }, [showLayersSaveDialog, autoLayersSave]);

  /**
   * saveLayerToServer
   * Saves the current edited layer to the server. Aborts early if GeoJSON is empty.
   */
  const saveLayerToServer = (localFileName) => {
    if (!localFileName) {
      localFileName = fileName;
    }

    setIsProgress(true);

    saveLayerToServerUnified({
      layer: currentLayer,
      userData,
      pointCloudStyle,
      description,
      createFileBaseName: removeEpochSuffix(removeFileExtension(localFileName)),
      createFileEpoch: timeStamp,
      validateNotEmpty: true,
    })
      .then((res) => {
        if (!res) {
          setIsProgress(false);
          return;
        }

        if (res.data.status == "failed") {
          setIsProgress(false);
          showToast(res.data.message, Type.NOTIFICATION.ERROR);
        } else {
          showToast(res.data.message, Type.NOTIFICATION.SUCCESS);

          setMapObjects((prev) => {
            const idx = prev.findIndex((x) => x.id === currentLayer.id);
            if (idx === -1) return prev;

            const next = prev.slice();
            const old = next[idx];

            next[idx] = {
              ...old,
              layer_id: res.data.layer_id,
              file_name: removeFileExtension(res.data.layer_name) + (currentLayer.type != Type.ASSET.IMAGERY ? old.file_type : ""),
              is_edited: false,
              original_geojson: getEditedGeoJSON(currentLayer),
            };

            return next;
          });

          setIsProgress(false);
          setShowLayersSaveDialog(false);
        }
      })
      .catch((err) => {
        setIsProgress(false);
        console.error("Error", err);
        showToast("Saving failed due to an unexpected error.", Type.NOTIFICATION.ERROR);
      })
      .finally(() => {
        setAutoLayersSave(false);
        setTimeStamp(Math.floor(Date.now() / 1000));
      });
  };

  return (
    <Dialog.Root
      lazyMount
      open={showLayersSaveDialog && !autoLayersSave}
      closeOnInteractOutside={false}
      onOpenChange={(e) => {
        setShowLayersSaveDialog(e.open);
        if (!Boolean(e.open)) {
          setCurrentLayer(null);
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Save To Server</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Input
                disabled
                placeholder="Enter name"
                value={fileName}
                onChange={(v) => {
                  setFileName(v.target.value);
                }}
              />
              <Field.Root mt={3}>
                <Field.Label>Description</Field.Label>
                <Textarea
                  maxLength={256}
                  rows={5}
                  value={description}
                  placeholder="Description ..."
                  variant="outline"
                  onChange={(v) => {
                    setDescription(v.target.value);
                  }}
                />
                <Field.HelperText>Max 256 characters.</Field.HelperText>
              </Field.Root>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </Dialog.ActionTrigger>
              <Button
                onClick={() => {
                  saveLayerToServer();
                }}
              >
                {isProgress ? <Spinner size="sm" /> : "Save"}
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default LayersSave;
